import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { SearchService } from '@zag/ui';
import { type ManagedPermission, type PermissionDetailsResponse, type PermissionsResponse } from '@zag/auth/models';

export type { ManagedPermission, PermissionDetails, PermissionRole } from '@zag/auth/models';

@Injectable({ providedIn: 'root' })
export class PermissionsManagementService {
  private readonly http = inject(HttpClient);
  private readonly searchService = inject(SearchService);
  private readonly basePath = '/api/v1/rbac/permissions';

  private readonly refreshTrigger = signal(0);
  readonly selectedId = signal<string | undefined>(undefined);

  private readonly listResource = httpResource<PermissionsResponse>(() => {
    this.refreshTrigger();
    // Track catalog search query to trigger reload when it changes
    const searchQuery = this.searchService.catalogSearchQuery();

    // Build URL with search parameter if present
    if (searchQuery) {
      return `${this.basePath}?search=${encodeURIComponent(searchQuery)}`;
    }
    return this.basePath;
  });

  private readonly selectedResource = httpResource<PermissionDetailsResponse>(() => {
    const id = this.selectedId();
    return id ? `${this.basePath}/${id}` : undefined;
  });

  readonly permissions = computed(() => this.listResource.value() ?? []);
  readonly isLoading = () => this.listResource.isLoading();
  readonly error = () => this.listResource.error();

  readonly selectedPermission = computed(() => this.selectedResource.value());
  readonly isLoadingSelected = () => this.selectedResource.isLoading();

  /**
   * Group permissions by resource (e.g., "plans" from "plans:read")
   */
  readonly permissionsByResource = computed(() => {
    const perms = this.permissions();
    const grouped: Record<string, ManagedPermission[]> = {};

    for (const perm of perms) {
      const [resource] = perm.id.split(':');
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(perm);
    }

    return grouped;
  });

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  getPermissionDetails(id: string) {
    return this.http.get<PermissionDetailsResponse>(`${this.basePath}/${id}`);
  }

  selectById(id: string): void {
    this.selectedId.set(id);
  }

  clearSelection(): void {
    this.selectedId.set(undefined);
  }
}
